import type { LegalDocPair } from '../types'

/**
 * Términos del programa Beta.
 *
 * véltica entera está en beta y algunas funciones se anuncian como «próximamente»
 * (cursos, bandeja de entrada, marketing por correo, los planes Business y
 * Creator). Este documento dice qué se puede esperar de eso y respalda el
 * descuento de lanzamiento. Las cifras del descuento salen de los mismos
 * valores que cobra el servidor (`legalPlaceholders`), no de este texto.
 */
export const BETA: LegalDocPair = {
  en: {
    title: 'Beta Program Terms',
    summary: 'What to expect from a product in beta, the launch discount, and features that are not available yet.',
    intro: [
      'véltica is currently in beta. These Beta Program Terms are part of the [Terms of Service](doc:terms) and apply to your use of the Platform while it is in beta and to any feature we label beta, preview or “coming soon”.',
    ],
    sections: [
      {
        title: 'What beta means',
        subs: [
          {
            title: 'The Platform and its features are in beta',
            blocks: [
              'The Platform is under active development. While it is in beta, features may be incomplete, may contain errors or may not work as described, and we may change, limit, rename or remove them at any time. You use the Platform in beta voluntarily.',
            ],
          },
          {
            title: 'No warranty and no service level',
            blocks: [
              'We provide beta features “as is”, without warranty and without any commitment on availability, performance or support times. The disclaimers and limits of liability in the [Terms of Service](doc:terms) apply fully. Because data can be lost or corrupted in a product that is still changing, keep your own copies of anything important.',
            ],
          },
          {
            title: 'Changes and end of the beta',
            blocks: [
              'We may end the beta or a beta feature, or move it to a paid plan, at any time. Where a change significantly affects something you rely on, we will try to give you notice where that is reasonably possible.',
            ],
          },
        ],
      },
      {
        title: 'Features that are not available yet',
        blocks: [
          'Some features and plans are shown as “coming soon” or as available at launch, such as selling courses, the email inbox, email marketing, and the Business and Creator plans. They are not part of the Platform today, we have no obligation to release them, and we may release them later with different features, limits or prices.',
          'Do not rely on a feature that is not available when you decide to buy. Descriptions of plans and limits for features that are not yet available are only plans, not promises, and they do not create any right for you.',
        ],
      },
      {
        title: 'Launch discount',
        subs: [
          {
            title: 'What it is',
            blocks: [
              'During the beta we offer a launch discount of {betaPercent}% off the regular annual price of paid plans, applied automatically at checkout, for the first {betaMonths} months of the subscription. It applies only to annual billing, not to monthly billing, domains or other purchases, and it cannot be combined with other discounts unless we say so.',
            ],
          },
          {
            title: 'After the discount',
            blocks: [
              'When the discounted period ends, your plan renews at the regular annual price in force then, and we will try to remind you before it renews. You can cancel before the renewal.',
            ],
          },
          {
            title: 'Changes to the offer',
            blocks: [
              'We may change or end the launch discount for new purchases at any time. A discount you already received is honored for the period stated when you purchased it. It has no cash value, is not transferable, and ends if you cancel or your subscription lapses.',
            ],
          },
        ],
      },
      {
        title: 'Feedback and confidentiality',
        blocks: [
          'We welcome your feedback and you agree that section 6.3 of the Terms applies to it. If we give you access to a feature that is not public, it is our Confidential Information under section 11 of the Terms, and you may not disclose it or describe it publicly without our permission. Your own Customer Content does not become confidential or ours because you use a beta feature.',
        ],
      },
      {
        title: 'Responsibility',
        blocks: [
          'TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR ANY LOSS OR DAMAGE ARISING FROM YOUR USE OF A BETA FEATURE, INCLUDING LOSS OF DATA, LOST SALES OR INTERRUPTION OF YOUR SITE, AND THE LIMITS IN SECTION 12 OF THE TERMS APPLY. If you cannot accept that risk for something important, do not depend on a beta feature for it.',
        ],
      },
    ],
  },

  es: {
    title: 'Términos del programa Beta',
    summary: 'Qué esperar de un producto en beta, el descuento de lanzamiento y las funciones que aún no están disponibles.',
    intro: [
      'véltica está actualmente en beta. Estos Términos del programa Beta forman parte de los [Términos de servicio](doc:terms) y se aplican a tu uso de la Plataforma mientras esté en beta y a cualquier función que marquemos como beta, vista previa o «próximamente».',
    ],
    sections: [
      {
        title: 'Qué significa beta',
        subs: [
          {
            title: 'La Plataforma y sus funciones están en beta',
            blocks: [
              'La Plataforma está en desarrollo activo. Mientras esté en beta, las funciones pueden estar incompletas, contener errores o no funcionar como se describe, y podemos cambiarlas, limitarlas, renombrarlas o retirarlas en cualquier momento. Usas la Plataforma en beta de forma voluntaria.',
            ],
          },
          {
            title: 'Sin garantía y sin nivel de servicio',
            blocks: [
              'Ofrecemos las funciones en beta «tal cual», sin garantía y sin ningún compromiso de disponibilidad, rendimiento o tiempos de soporte. Las exclusiones de garantía y los límites de responsabilidad de los [Términos de servicio](doc:terms) aplican por completo. Como en un producto que aún cambia se pueden perder o corromper datos, guarda tus propias copias de lo importante.',
            ],
          },
          {
            title: 'Cambios y fin de la beta',
            blocks: [
              'Podemos terminar la beta o una función en beta, o llevarla a un plan de pago, en cualquier momento. Cuando un cambio afecte de forma importante a algo en lo que confías, intentaremos avisarte cuando sea razonablemente posible.',
            ],
          },
        ],
      },
      {
        title: 'Funciones que aún no están disponibles',
        blocks: [
          'Algunas funciones y planes se muestran como «próximamente» o como disponibles en el lanzamiento, como la venta de cursos, la bandeja de entrada de correo, el marketing por correo y los planes Business y Creator. No forman parte de la Plataforma hoy, no tenemos obligación de lanzarlos y podemos lanzarlos más adelante con funciones, límites o precios distintos.',
          'No confíes en una función que no está disponible cuando decidas comprar. Las descripciones de planes y límites de funciones aún no disponibles son solo planes, no promesas, y no te crean ningún derecho.',
        ],
      },
      {
        title: 'Descuento de lanzamiento',
        subs: [
          {
            title: 'En qué consiste',
            blocks: [
              'Durante la beta ofrecemos un descuento de lanzamiento de {betaPercent}% sobre el precio anual normal de los planes de pago, que se aplica automáticamente al pagar, durante los primeros {betaMonths} meses de la suscripción. Aplica solo al cobro anual, no al mensual, a los dominios ni a otras compras, y no se puede combinar con otros descuentos salvo que lo indiquemos.',
            ],
          },
          {
            title: 'Después del descuento',
            blocks: [
              'Cuando termina el periodo con descuento, tu plan se renueva al precio anual normal vigente entonces, e intentaremos recordártelo antes de la renovación. Puedes cancelar antes de que se renueve.',
            ],
          },
          {
            title: 'Cambios en la oferta',
            blocks: [
              'Podemos cambiar o terminar el descuento de lanzamiento para compras nuevas en cualquier momento. Un descuento que ya recibiste se respeta durante el periodo indicado cuando lo compraste. No tiene valor en efectivo, no es transferible y termina si cancelas o tu suscripción caduca.',
            ],
          },
        ],
      },
      {
        title: 'Comentarios y confidencialidad',
        blocks: [
          'Agradecemos tus comentarios y aceptas que les aplica el apartado 6.3 de los Términos. Si te damos acceso a una función que no es pública, es Información confidencial nuestra conforme al apartado 11 de los Términos, y no puedes revelarla ni describirla públicamente sin nuestro permiso. Tu propio Contenido del cliente no pasa a ser confidencial ni nuestro por usar una función en beta.',
        ],
      },
      {
        title: 'Responsabilidad',
        blocks: [
          'EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, NO RESPONDEMOS POR NINGUNA PÉRDIDA O DAÑO DERIVADO DE TU USO DE UNA FUNCIÓN EN BETA, INCLUIDAS LA PÉRDIDA DE DATOS, LAS VENTAS PERDIDAS O LA INTERRUPCIÓN DE TU SITIO, Y APLICAN LOS LÍMITES DEL APARTADO 12 DE LOS TÉRMINOS. Si no puedes aceptar ese riesgo para algo importante, no dependas de una función en beta para ello.',
        ],
      },
    ],
  },
}
